/**
 * MODULE: AssistantEmailDraftCard.tsx
 * PURPOSE: Interactive email draft card in assistant replies — edit, copy, AI improve, save as template.
 * RECENT: 2026-06-06 [2.2.1330] — Pen edit, copy, AI improve, save-as-template, email library link.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 175 / 200 lines
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../../design-system/components/Feedback/toastContext';
import {
  buildEmailDraftImprovePrompt,
  EMAIL_TEMPLATES_MODULE_URL,
  formatAssistantEmailDraftCopy,
  type ParsedAssistantEmailDraft,
} from '../../design-system/components/AiAssistant/assistantEmailDraftUtils';
import type { EmailTemplateCreatePrefill } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';

interface AssistantEmailDraftCardProps {
  draft: ParsedAssistantEmailDraft;
  disabled?: boolean;
  onImprove: (prompt: string) => void;
  onSaveAsTemplate: (prefill: EmailTemplateCreatePrefill) => void;
}

export function AssistantEmailDraftCard({
  draft,
  disabled = false,
  onImprove,
  onSaveAsTemplate,
}: AssistantEmailDraftCardProps) {
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);

  useEffect(() => {
    setSubject(draft.subject);
    setBody(draft.body);
    setIsEditing(false);
  }, [draft.subject, draft.body, draft.fullText]);

  const currentCopy = formatAssistantEmailDraftCopy({
    intro: draft.intro,
    subject,
    body,
    outro: draft.outro,
  });

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentCopy);
      showToast({ message: 'Email draft copied', variant: 'success' });
    } catch {
      showToast({ message: 'Could not copy to clipboard', variant: 'error' });
    }
  }, [currentCopy, showToast]);

  const handleImprove = () => {
    onImprove(buildEmailDraftImprovePrompt({ subject, body }));
  };

  const handleSaveAsTemplate = () => {
    onSaveAsTemplate({
      name: subject.slice(0, 80) || 'Assistant email draft',
      subject,
      bodyContent: body,
      category: 'follow-up',
    });
  };

  return (
    <div
      className="relative rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-900/60"
      data-testid="assistant-email-draft-card"
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-3 py-2.5 dark:border-gray-600">
        <div className="min-w-0 flex-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">
          {draft.intro ? <p className="mb-0">{draft.intro}</p> : <p className="mb-0 font-medium">Email draft</p>}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsEditing((v) => !v)}
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-primary-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-primary-400"
            aria-label={isEditing ? 'Finish editing email draft' : 'Edit email draft'}
            title={isEditing ? 'Done editing' : 'Edit draft'}
            data-testid="assistant-email-draft-edit"
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => void handleCopy()}
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-primary-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-primary-400"
            aria-label="Copy email draft"
            title="Copy draft"
            data-testid="assistant-email-draft-copy"
          >
            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="space-y-3 px-3 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Subject
          </p>
          {isEditing ? (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Email subject"
            />
          ) : (
            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-50">{subject}</p>
          )}
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Body
          </p>
          {isEditing ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="mt-1 w-full resize-y rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm leading-relaxed text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Email body"
            />
          ) : (
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-800 dark:text-gray-200">
              {body}
            </p>
          )}
        </div>
      </div>

      {draft.outro ? (
        <p className="border-t border-gray-200 px-3 py-2.5 text-[12px] leading-relaxed text-gray-600 dark:border-gray-600 dark:text-gray-400">
          {draft.outro}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 px-3 py-2.5 dark:border-gray-600">
        <button
          type="button"
          disabled={disabled}
          onClick={handleImprove}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-800 transition hover:bg-primary-100 disabled:opacity-50 dark:border-primary-800 dark:bg-primary-950/50 dark:text-primary-200 dark:hover:bg-primary-950"
          data-testid="assistant-email-draft-improve"
        >
          <SparklesIcon className="h-3.5 w-3.5" aria-hidden />
          AI Improve
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={handleSaveAsTemplate}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
          data-testid="assistant-email-draft-save-template"
        >
          Save as template
        </button>
        <a
          href={EMAIL_TEMPLATES_MODULE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 underline decoration-primary-600/40 underline-offset-2 hover:decoration-primary-600 dark:text-primary-400 dark:decoration-primary-400/40"
          data-testid="assistant-email-draft-templates-link"
        >
          Email templates
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
    </div>
  );
}
